<template>
    <div class="asset-explorer-sidebar shadow-sm">
        <!-- Logo Header (Match Argon Sidenav) -->
        <div class="sidenav-header p-3 mb-2">
            <router-link class="m-0 navbar-brand" :to="{ name: 'ModuleSelection' }">
                <img
:src="`${apiUrl + '/smartcat/logo.png'}`" class="navbar-brand-img h-100" alt="main_logo"
                    style="max-height: 40px;" />
            </router-link>
        </div>
        <hr class="mt-0 horizontal dark" />

        <!-- Search -->
        <div class="explorer-search px-3">
            <IconField>
                <InputIcon>
                    <i class="pi pi-search" />
                </InputIcon>
                <InputText
v-model="explorerSearch" type="text" size="small" placeholder="Search assets..."
                    class="w-100" />
            </IconField>
        </div>

        <!-- Mega Menu Tree -->
        <div class="explorer-menu" style="flex: 1; overflow-y: auto;" @click.self="resetSelection">
            <!-- Skeleton Loading -->
            <div v-if="loading" class="px-3 py-2">
                <div v-for="n in 8" :key="n" class="d-flex align-items-center gap-2 mb-3">
                    <Skeleton shape="circle" size="1.2rem" />
                    <Skeleton width="70%" height="0.9rem" />
                </div>
            </div>

            <!-- Empty State -->
            <div v-else-if="filteredCategories.length === 0" class="d-flex flex-column align-items-center justify-content-center text-muted px-3 py-5">
                <i class="pi pi-inbox mb-2" style="font-size: 2rem; opacity: 0.3;"></i>
                <span class="fw-semibold" style="font-size: 0.85rem;">No assets found</span>
                <span style="font-size: 0.75rem;" class="text-center mt-1">{{ explorerSearch ? 'Try a different search keyword' : 'No asset data available' }}</span>
            </div>

            <div v-for="(node, idx) in filteredCategories" v-else :key="node.asset_code || idx" class="menu-category">
                <div
class="menu-node" :style="{ paddingLeft: '24px' }"
                    :class="{ active: selectedItem === node.asset_code }"
                    @click="onNodeClick(node)">
                    <span class="menu-category-icon">
                        <i v-if="node.loading" class="pi pi-spin pi-spinner"></i>
                        <i v-else :class="node.icon"></i>
                    </span>
                    <span class="menu-node-label flex-grow-1">{{ node.label }}</span>
                    <span v-if="node.has_children" class="menu-sub-arrow">
                        <i :class="node.open ? 'pi pi-chevron-down' : 'pi pi-chevron-right'"></i>
                    </span>
                </div>
                <!-- Recursive children -->
                <transition name="slide-down">
                    <div v-if="node.open && node.children && node.children.length > 0">
                        <asset-tree-node
                            v-for="(child, cIdx) in node.children"
                            :key="child.asset_code || cIdx"
                            :node="child"
                            :depth="1"
                            :selected-item="selectedItem"
                            @node-click="onNodeClick"
                        />
                    </div>
                </transition>
            </div>
        </div>

        <!-- Explorer Footer -->
        <div class="explorer-footer p-3 border-top bg-white mt-auto">
            <div class="d-flex justify-content-center mb-2 text-muted" style="font-size: 0.70rem;">
                <span>Total: {{ totalAssets }} Assets</span>
            </div>
            <button
                class="btn btn-primary btn-sm w-100 fw-bold d-flex align-items-center justify-content-center gap-2 shadow-sm transition-all"
                @click="$emit('add-asset', selectedParentNode)">
                <i class="pi pi-plus" style="font-size: 0.8rem;"></i> Add Asset
            </button>
        </div>
    </div>
</template>

<script>
import baseApiUrl from '@/configs/config';
import AssetTreeNode from './AssetTreeNode.vue';

export default {
    name: "AssetExplorer",
    components: { AssetTreeNode },
    props: {
        categories: {
            type: Array,
            required: true
        },
        loading: {
            type: Boolean,
            default: false
        }
    },
    emits: ["select-item", "load-children", "add-asset", "select-folder", "search"],
    data() {
        return {
            explorerSearch: "",
            selectedItem: null,
            selectedParentNode: null,
            apiUrl: baseApiUrl.storageUrl,
            searchDebounce: null,
        };
    },
    computed: {
        filteredCategories() {
            return this.categories;
        },
        totalAssets() {
            let count = 0;
            const countAll = (nodes) => {
                for (let node of nodes) {
                    count++;
                    if (node.children && node.children.length > 0) {
                        countAll(node.children);
                    }
                }
            };
            countAll(this.categories);
            return count;
        }
    },
    watch: {
        explorerSearch(val) {
            clearTimeout(this.searchDebounce);
            this.searchDebounce = setTimeout(() => {
                this.$emit("search", val.trim());
            }, 350);
        },
    },
    methods: {
        filterNodes(nodes, q) {
            return nodes.filter(node => {
                const match = node.label.toLowerCase().includes(q);
                if (node.children && node.children.length > 0) {
                    const filteredChildren = this.filterNodes(node.children, q);
                    return match || filteredChildren.length > 0;
                }
                return match;
            });
        },
        resetSelection() {
            this.selectedItem = null;
            this.selectedParentNode = null;
            this.$emit("select-folder", null);
        },
        onNodeClick(node) {
            this.selectedItem = node.asset_code;
            const isFolder = node.asset_type === "funcloc" || node.asset_type === "system";
            if (!node.has_children) {
                this.selectedParentNode = node.asset_type === "equipment" ? node : null;
                this.$emit("select-item", node);
                return;
            }
            const needsLoad = !node.open && !node.children?.length;
            node.open = !node.open;
            this.selectedParentNode = node;
            if (isFolder) {
                if (needsLoad) this.$emit("load-children", node);
                this.$emit("select-folder", node);
            } else {
                // equipment / sub_equipment: detail call already returns children, skip load-children
                this.$emit("select-item", node);
            }
        },
    }
};
</script>

<style scoped>
/* ===== Asset Explorer Sidebar ===== */
.asset-explorer-sidebar {
    position: fixed;
    top: 0;
    left: 0;
    width: 250px;
    height: 100vh;
    background: #ffffff;
    border-right: 1px solid #e9ecef;
    display: flex;
    flex-direction: column;
    z-index: 999;
}

/* Search */
.explorer-search {
    padding: 0 16px 16px;
}

.explorer-search :deep(.p-inputtext) {
    font-size: 13px;
    border-radius: 8px;
    border-color: #d2d6da;
    background: #fff;
    padding: 0.4rem 0.5rem;
}

.explorer-search :deep(.p-inputtext:focus) {
    border-color: #5e72e4;
    box-shadow: 0 0 0 2px rgba(94, 114, 228, 0.2);
}

/* Menu Scrollbar */
.explorer-menu::-webkit-scrollbar {
    width: 3px;
}

.explorer-menu::-webkit-scrollbar-thumb {
    background: #e9ecef;
    border-radius: 10px;
}

/* Node Item */
.menu-node {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 24px;
    cursor: pointer;
    transition: all 0.2s ease;
    user-select: none;
    font-size: 0.8rem;
    color: #67748e;
}

.menu-node:hover {
    color: #344767;
    background: #f8f9fa;
}

.menu-node.active {
    background: #f6f9ff;
    color: #5e72e4;
    font-weight: 600;
}

.menu-node-label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.menu-category-icon i {
    font-size: 14px;
    color: #5e72e4;
}

.menu-sub-arrow i {
    font-size: 10px;
    color: #8898aa;
}

/* Decorative HR */
.horizontal.dark {
    background-image: linear-gradient(90deg, transparent, rgba(0, 0, 0, 0.4), transparent);
    height: 1px;
    margin: 0;
    border: none;
}

/* Transition */
.slide-down-enter-active,
.slide-down-leave-active {
    transition: all 0.2s ease;
    overflow: hidden;
    max-height: 500px;
}

.slide-down-enter-from,
.slide-down-leave-to {
    max-height: 0;
    opacity: 0;
}
</style>
